<template>
	<div class="flex flex-col h-max px-4 py-7 md:p-dj-40 border border-dj-gray-light/50 rounded-big bg-white">
		<div class="hidden md:flex">
			<BaseCloudImage
				custom-class="shrink-0 rounded-normal overflow-hidden h-max"
				:public-id="logo"
				postfix=".jpg"
				alt="company logo"
				:transformation="[{ width: 80, height: 80, crop: 'scale' }]"
			/>
			<div class="flex flex-col ml-4">
				<span class="mb-2 text-xl font-medium">
					{{ name }}
				</span>
				<span class="text-dj-gray-dark text-sm">
					{{ location }}
				</span>
			</div>
		</div>

		<div class="hidden md:block h-[1px] w-full my-5 bg-dj-gray-light"></div>

		<div class="flex flex-col">
			<span class="text-sm md:text-base mx-auto mb-5">{{ $t("company.card.jobs", { jobs: jobs }) }}</span>
			<BaseButton @click="scrollToJobs" type="primary-red" :disabled="jobs == 0">
				{{ $t("company.card.checkTheJobs") }}
			</BaseButton>
		</div>

		<div class="h-[1px] w-full my-5 bg-dj-gray-light"></div>

		<CompanyProfileFollowCompanySection
			:location="location"
			:companySlug="companySlug"
			:companyId="companyId"
		></CompanyProfileFollowCompanySection>

		<!-- SZMD Vote Section - for nominated companies -->
		<template v-if="szmdInlineVote && szmdVotingInProgress && !isSzmdVotingPage && isCompanyNominated">
			<div class="h-[1px] w-full my-5 bg-dj-gray-light"></div>
			<div class="flex flex-col text-center text-dj-gray-dark">
				<!-- Step 1: Star Rating -->
				<template v-if="szmdVoteStep === 'rating'">
					<span class="font-medium">{{ $t("szmdTexts.rating.lovelableThisCompany") }}</span>
					<div class="mt-4 p-3 bg-dj-red rounded-lg">
						<StarRating :id="companyId" :rating="0" @ratingSelected="handleStarRating" />
					</div>
				</template>

				<!-- Step 2: Would you work here? -->
				<template v-else-if="szmdVoteStep === 'workHere'">
					<span class="font-medium">{{ $t("szmdTexts.rating.workHere") }}</span>
					<div class="mt-4 flex gap-3 justify-center">
						<BaseButton @click="handleWorkHere(true)" type="primary-red" class="px-8">
							{{ $t("yes") }}
						</BaseButton>
						<BaseButton @click="handleWorkHere(false)" type="secondary" class="px-8">
							{{ $t("no") }}
						</BaseButton>
					</div>
				</template>

				<!-- Loading state -->
				<template v-else-if="szmdVoteStep === 'loading'">
					<div class="mt-4 text-dj-gray">
						<span class="dj-loader-small mr-2"></span>
						{{ $t("loading") }}
					</div>
				</template>

				<!-- Success state -->
				<template v-else-if="szmdVoteStep === 'success' || szmdVoteStep === 'already_voted'">
					<div class="mt-4 text-green-600 font-medium">
						{{ $t("szmdTexts.voteSuccess") }}
					</div>
				</template>

				<!-- Error state -->
				<template v-else-if="szmdVoteStep === 'error'">
					<div class="mt-4 text-red-600">
						{{ szmdVoteError }}
					</div>
				</template>

				<!-- Login required -->
				<template v-else-if="szmdVoteStep === 'login_required'">
					<span class="font-medium mb-2">{{ $t("szmdTexts.rating.lovelableThisCompany") }}</span>
					<BaseButton @click="goToLogin" type="primary-red" class="w-full">
						{{ $t("loginBtn") }}
					</BaseButton>
				</template>
			</div>
		</template>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from "vue";
import axios from "axios";

export default defineComponent({
	name: "CompanyProfileCard",
	props: {
		name: {
			type: String,
			default: "Company",
		},
		location: {
			type: String,
			default: "company address",
		},
		logo: {
			type: String,
		},
		jobs: {
			type: Number,
			default: 0,
		},
		companySlug: {
			type: String,
			required: true,
		},
		companyId: {
			type: Number,
			required: true,
		},
		szmd: {
			type: Boolean,
			default: null,
		},
	},
	setup(props) {
		const { locale, t } = useI18n();
		const isAuth = useState("isAuth");
		const router = useRouter();
		const route = useRoute();
		const gtm = useGTM();

		// SZMD inline voting (enabled per region in szmdConfig)
		const { szmdVotingInProgress, isSzmdVotingPage, szmdInlineVote } = useSzmdConfig();

		// Check if company is nominated for SZMD (szmd === true)
		const isCompanyNominated = computed(() => props.szmd);

		// SZMD vote flow state
		type VoteStep = "rating" | "workHere" | "loading" | "success" | "error" | "login_required" | "already_voted";
		const szmdVoteStep = ref<VoteStep>("rating");
		const szmdVoteError = ref("");
		const pendingRating = ref<number>(0);

		const checkAlreadyVoted = async () => {
			if (!isAuth.value || !szmdInlineVote || !szmdVotingInProgress || !isCompanyNominated.value) return;

			try {
				const response = await axios.get(
					`${globalConfig.API}/szmd/my-ratings?region=${globalConfig.region.toLowerCase()}`,
					{
						headers: {
							Authorization: `Bearer ${useCookie("token").value}`,
						},
						withCredentials: true,
					}
				);

				const ratedCompanies = response.data?.ratedCompanies || [];
				if (ratedCompanies.includes(Number(props.companyId))) {
					szmdVoteStep.value = "already_voted";
				}
			} catch (e) {
				console.error("Error checking already voted status:", e);
			}
		};

		// Watch for auth state changes to update the vote step
		watch(
			isAuth,
			(authenticated) => {
				if (szmdInlineVote && szmdVotingInProgress && isCompanyNominated.value) {
					if (authenticated) {
						if (szmdVoteStep.value === "login_required") {
							szmdVoteStep.value = "rating";
						}
						checkAlreadyVoted();
					} else {
						szmdVoteStep.value = "login_required";
					}
				}
			},
			{ immediate: true }
		);

		function scrollToJobs() {
			const el = document.getElementById("company-profile-carousel").offsetTop;
			window.scrollTo({ top: el, left: 0, behavior: "smooth" });
		}

		function goToLogin() {
			router.push({ path: `/${locale.value}/login`, query: { r: route.fullPath } });
		}

		// Step 1: Handle star rating selection
		function handleStarRating(rating: number) {
			if (!isAuth.value) {
				szmdVoteStep.value = "login_required";
				return;
			}

			pendingRating.value = rating;

			// Check if user already follows this company
			if (useAuth.isLovedCompany(props.companyId)) {
				// Skip "work here" question and submit directly
				submitVote(rating);
			} else {
				// Show "Would you work here?" question
				szmdVoteStep.value = "workHere";
			}
		}

		// Step 2: Handle "Would you work here?" answer
		function handleWorkHere(wouldWork: boolean) {
			if (wouldWork) {
				// Save company to favorites
				useAuth.storeSavedCompany(props.companyId);
			}
			// Submit the vote
			submitVote(pendingRating.value);
		}

		// Submit vote to API
		async function submitVote(rating: number) {
			szmdVoteStep.value = "loading";
			szmdVoteError.value = "";

			try {
				await axios.post(
					`${globalConfig.API}/szmd/close-round?region=${globalConfig.region.toLowerCase()}`,
					{
						data: [
							{
								company_id: props.companyId,
								rate: rating,
							},
						],
					},
					{
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${useCookie("token").value}`,
							Accept: "application/json",
						},
						withCredentials: true,
					}
				);

				szmdVoteStep.value = "success";
				gtm.trackEvent({ event: "szmd-vote-from-company-profile" });
			} catch (error: unknown) {
				console.error("SZMD vote error:", error);
				szmdVoteStep.value = "error";

				if (error.response?.status === 401) {
					szmdVoteStep.value = "login_required";
				} else if (error.response?.data?.error === "voting_not_available") {
					szmdVoteError.value = t("szmdTexts.votingNotAvailable.description");
				} else {
					szmdVoteError.value = t("errors.somethingWentWrong");
				}
			}
		}

		return {
			scrollToJobs,
			szmdInlineVote,
			szmdVotingInProgress,
			isSzmdVotingPage,
			isCompanyNominated,
			szmdVoteStep,
			szmdVoteError,
			handleStarRating,
			handleWorkHere,
			goToLogin,
		};
	},
});
</script>

<style scoped>
.dj-loader-small {
	width: 16px;
	height: 16px;
	border: 2px solid currentColor;
	border-bottom-color: transparent;
	vertical-align: middle;
	border-radius: 50%;
	display: inline-block;
	animation: rotation 1s linear infinite;
}

@keyframes rotation {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}
</style>
